import { Injectable, inject } from '@angular/core'
import { NavigationExtras, Router } from '@angular/router'
import { ConfiguratorProductQueryParams } from '../../modules/dashboard/configurator/configurator-product-query-params'
import { firstValueFrom, forkJoin, Observable } from 'rxjs'
import { ShopService } from './shop.service'
import { map, mergeMap } from 'rxjs/operators'
import { DeprecatedConfiguratorComponentQueryParams } from '../../modules/dashboard-old/configurator/configurator.model'

@Injectable({ providedIn: 'root' })
export class NavigationService {
	private readonly router = inject(Router)
	private readonly shopService = inject(ShopService)

	goTo(path: string, extras?: NavigationExtras): Promise<boolean> {
		return this.router.navigate([path], extras)
	}

	goToHome(): Promise<boolean> {
		return this.goTo('')
	}

	goToLogin(url: string = ''): Promise<boolean> {
		return url === '' ? this.goTo('auth') : this.goTo('auth', { queryParams: { url } })
	}

	goToAdminUsers(): Promise<boolean> {
		return this.goTo('admin/users')
	}

	goToAdminOrders(): Promise<boolean> {
		return this.goTo('admin/orders')
	}

	goToAdminInvoices(): Promise<boolean> {
		return this.goTo('admin/invoices')
	}

	goToAdminShoes(): Promise<boolean> {
		return this.goTo('admin/shoes')
	}

	goToFinancialOrders(): Promise<boolean> {
		return this.goTo('financial/orders')
	}

	goToFinancialInvoices(): Promise<boolean> {
		return this.goTo('financial/invoices')
	}

	goToEditorShoes(): Promise<boolean> {
		return this.goTo('editor/shoes')
	}

	goToBusinessClientOrders(): Promise<boolean> {
		return this.goTo('business/orders')
	}

	goToBusinessClientSellers(): Promise<boolean> {
		return this.goTo('business/sellers')
	}

	goToBusinessClientShoes(): Promise<boolean> {
		return this.goTo('business/shoes')
	}

	goToDashboard(): Observable<boolean> {
		return this.getShopBaseRoute().pipe(mergeMap((route) => this.goTo(route)))
	}

	async goToShoeConfigurator(queryParams: ConfiguratorProductQueryParams): Promise<boolean> {
		const baseShopRoute = await firstValueFrom(this.getShopBaseRoute())
		await this.goTo(`${baseShopRoute}/configurator`, { queryParams })
		location.reload()
		return true
	}

	goToOldShoeConfigurator(queryParams: DeprecatedConfiguratorComponentQueryParams): Observable<boolean> {
		return this.getOldShopBaseRoute().pipe(mergeMap((route) => this.goTo(`${route}/configurator`, { queryParams })))
	}

	goToPageNotFound(): Promise<boolean> {
		return this.goTo('404')
	}

	private getShopBaseRoute(): Observable<string> {
		return forkJoin([this.shopService.isPublicShop$, this.shopService.publicShopUrl$]).pipe(
			map(([isPublicShop, publicShopUrl]) =>
				isPublicShop ? `${ShopService.PUBLIC_SHOP_URL_BASE_PATH}/${publicShopUrl}` : 'dashboard',
			),
		)
	}

	private getOldShopBaseRoute(): Observable<string> {
		return forkJoin([this.shopService.isPublicShop$, this.shopService.publicShopUrl$]).pipe(
			map(([isPublicShop, publicShopUrl]) =>
				isPublicShop ? `${ShopService.PUBLIC_SHOP_URL_BASE_PATH}/${publicShopUrl}` : 'dashboard-old',
			),
		)
	}
}
